import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAllDoctor } from '@/services/doctorService';
import { fetchMedicalHistoryByDate, postMedicalHistory } from '@/services/medicalHistoryService';
import { setDoctorsRedux } from '@/redux/actions/doctorActions';
import { fetchPersonalData } from '@/services/personalService';
import { applyFacturacionDatosToHistoriaPayload } from '@/utils/consultaFacturacionPayload';
import emailjs from '@emailjs/browser';
import * as Yup from 'yup';

export const useHome = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState(null);
  const [filteredDoctorList, setFilteredDoctorList] = useState([]);
  const [selectedSpeciality, setSelectedSpeciality] = useState('TODAS');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [doctorServices, setDoctorServices] = useState([]);
  const [patientsxUser, setPatientsxUser] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [isLoadingPersonal, setIsLoadingPersonal] = useState(false);
  const [isLoadingPatientsxUser, setIsLoadingPatientsxUser] = useState(false);
  const [personalError, setPersonalError] = useState(null);
  const [patientInformation, setPatientInformation] = useState(null);
  const [loadingSaveHistory, setLoadingSaveHistory] = useState(false);
  const [isLoadingSpeciality, setIsLoadingSpeciality] = useState(false);
  const [isLoadingDoctor, setIsLoadingDoctor] = useState(false);

  const validationSchemaFacturacion = Yup.object().shape({
    documentType: Yup.string().required('El tipo de documento es obligatorio'),
    documentNumber: Yup.string().required('El número de documento es obligatorio'),
    fullName: Yup.string().required('El nombre completo es obligatorio'),
  });

  const now = new Date();
  const formattedNow = now.toISOString().slice(0, 10);

  const staticFilters = {
    desde: formattedNow,
    hasta: '2027-06-28',
  };

  const selectSchedule = (schedule) => {
    setSelectedSchedule(schedule);
  };

  const selectService = (service) => {
    console.log('seleccionando');
    setSelectedService(service);
  };

  const loadDoctors = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchAllDoctor();

      const medicosActivosOrdenados =
        response?.medicos
          ?.filter((m) => String(m.est_personal) === '1' && m.orden_externo !== null)
          ?.sort((a, b) => {
            const ordenA = Number(a.orden_externo) || 0;
            const ordenB = Number(b.orden_externo) || 0;
            return ordenA - ordenB;
          }) || [];

      setDoctors(medicosActivosOrdenados);
      dispatch(setDoctorsRedux(medicosActivosOrdenados));
      setFilteredDoctorList(medicosActivosOrdenados);
    } catch (err) {
      setError(err.message || 'Error cargando médicos');
    } finally {
      setIsLoading(false);
    }
  };

  const listaEspecialidades = useMemo(() => {
    if (!doctors) return [];
    return Array.from(
      new Set(
        doctors
          .map((d) => d.especialidad)
          .filter(Boolean)
          .map((e) => e.trim().toUpperCase()),
      ),
    );
  }, [doctors]);

  const changueEspFilter = async (esp, isFromCbo) => {
    setIsLoadingSpeciality(true);

    const especialidad = esp?.trim().toUpperCase();
    const list =
      !especialidad || especialidad === 'TODAS'
        ? doctors
        : doctors.filter((d) => d.especialidad?.trim().toUpperCase() === especialidad);

    setSelectedSpeciality(esp);
    setFilteredDoctorList(list);
    try {
      if (list.length > 0) {
        if (isFromCbo) {
          await changeDoctorFilter(list[0].codigo);
        }
      } else {
        setSelectedDoctor(null);
        setMedicalHistory([]);
        setPersonalData(null);
        setDoctorServices([]);
        setPatientsxUser([]);
        setSelectedService(null);
      }
    } catch (error) {
      console.error('Error en el filtro:', error);
    } finally {
      // 3. Se ejecuta solo cuando todo lo anterior terminó
      setIsLoadingSpeciality(false);
    }
  };

  const loadPersonalByDoctor = async (doctor) => {
    if (!doctor?.numero_de_dni || !doctor?.codigo_personal) return;

    try {
      setIsLoadingPersonal(true);
      setPersonalError(null);

      const userDataStr = localStorage.getItem('user');
      const authDataStr = localStorage.getItem('auth_data');

      let userId = null;

      if (userDataStr) {
        const parsedUser = JSON.parse(userDataStr);
        userId = parsedUser?.id ?? null;
      }

      if (!userId && authDataStr) {
        const parsedAuth = JSON.parse(authDataStr);
        userId = parsedAuth?.user?.id ?? null;
      }

      if (!userId) {
        throw new Error('No se pudo obtener el usuario logueado');
      }

      const response = await fetchPersonalData({
        numero_de_dni: doctor.numero_de_dni,
      });

      setPersonalData(response);
      const pacientes = Array.isArray(response?.pacientes) ? response.pacientes : [];
      const pacientesAsociados = pacientes.filter((p) => Number(p.user_id) === Number(userId));

      console.log('Pacientes asociados al usuario logueado:', pacientesAsociados);

      const serviciosFiltrados =
        response?.servicios?.filter((s) => {
          const matchMedico = String(s.medico_asociado) === String(doctor.codigo_personal);
          const matchEstado = String(s.estado_exter) === '5';
          return matchMedico && matchEstado;
        }) || [];

      setDoctorServices(serviciosFiltrados);
      setPatientsxUser(pacientesAsociados);
      setSelectedService((prev) => {
        if (prev && serviciosFiltrados.some((s) => s.codigo === prev.codigo)) {
          return prev;
        }
        return null;
      });
      setIsLoadingPersonal(false);
    } catch (err) {
      console.error('Error cargando datos de personal:', err);
      setPersonalError(err?.message || 'Error cargando datos de personal');
      setPersonalData(null);
      setDoctorServices([]);
      setSelectedService(null);
      setIsLoadingPersonal(false);
    } finally {
      setIsLoadingPersonal(false);
    }
  };

  const changeDoctorFilter = async (doctorCodigoSeleccionado) => {
    setIsLoadingHistory(true);
    const doctor = doctors.find((d) => String(d.codigo) === String(doctorCodigoSeleccionado));
    if (!doctor) {
      setSelectedDoctor(null);
      setMedicalHistory([]);
      setPersonalData(null);
      setDoctorServices([]);
      setPatientsxUser([]);
      setSelectedService(null);
      return;
    }

    setSelectedDoctor(doctor);
    loadPersonalByDoctor(doctor);

    try {
      setHistoryError(null);
      const response = await fetchMedicalHistoryByDate({
        ...staticFilters,
        num: 1550,
        medico: {
          codigo: doctor.codigo,
          codigo_personal: String(doctor.codigo_personal),
          direccion: doctor.direccion || '',
          especialidad: doctor.especialidad || '',
        },
      });
      setMedicalHistory(response?.historias?.data || []);
      setIsLoadingHistory(false);
      setIsLoadingDoctor(false);
    } catch (err) {
      setHistoryError(err.message || 'Error cargando historia médica');
      setMedicalHistory([]);
      setIsLoadingHistory(false);
    }
  };

  const getPatientsForDoctor = async (doctorCodigoSeleccionado) => {
    const doctor = doctors.find((d) => String(d.codigo) === String(doctorCodigoSeleccionado));

    if (!doctor?.numero_de_dni || !doctor?.codigo_personal) return;

    try {
      setIsLoadingPatientsxUser(true);
      const userDataStr = localStorage.getItem('user');
      const authDataStr = localStorage.getItem('auth_data');
      let userId = null;

      if (userDataStr) {
        const parsedUser = JSON.parse(userDataStr);
        userId = parsedUser?.id ?? null;
      }

      if (!userId && authDataStr) {
        const parsedAuth = JSON.parse(authDataStr);
        userId = parsedAuth?.user?.id ?? null;
      }

      if (!userId) {
        throw new Error('No se pudo obtener el usuario logueado');
      }

      const response = await fetchPersonalData({
        numero_de_dni: doctor.numero_de_dni,
      });

      const pacientes = Array.isArray(response?.pacientes) ? response.pacientes : [];
      const pacientesAsociados = pacientes.filter((p) => Number(p.user_id) === Number(userId));

      console.log('Pacientes asociados al usuario logueado:', pacientesAsociados);
      setPatientsxUser(pacientesAsociados);
      setIsLoadingPatientsxUser(false);
    } catch (err) {
      console.error('Error cargando datos de personal:', err);
      setIsLoadingPatientsxUser(false);
    }
  };

  const saveMedicalHistory = async (selectedDoctor, selectedSchedule, facturacionValues = null) => {
    setLoadingSaveHistory(true);
    if (!selectedService) {
      console.log('acá');
      return;
    }
    console.log('ver el paciente: ', patientInformation);
    try {
      const historiaBase = {
        //codigo: -1,
        codigo: selectedSchedule?.historyDetails?.codigo,
        numero_de_historia_clinica: patientInformation?.numero_de_historia_clinica,
        pacientes_codigo: patientInformation?.codigo_de_paciente,
        personal_codigo: selectedDoctor?.codigo_personal,
        tipo_de_consulta: selectedService?.codigo,
        monto_cobrado: selectedService?.costo_de_servicio,
        costo_de_consulta: selectedService?.costo_de_servicio,
        tipo_de_estado_codigo: 1,
        estado_de_horario: 'RESERVADO',
        fecha_de_atencion: selectedSchedule?.fecha,
        hora_de_atencion: selectedSchedule?.hora + ':00',
        fecha_de_registro: now.toLocaleDateString('en-CA'),
        hora_de_registro: now.toLocaleTimeString('en-GB'),
        tipo_de_reserva: 'EXTERNO',
        duracion_de_atencion: '00:30:00',
        fecha_de_reserva_atc: now.toLocaleDateString('en-CA'),
        hora_de_reserva_atc: now.toLocaleTimeString('en-GB'),
      };

      const payloadHistoria = facturacionValues
        ? applyFacturacionDatosToHistoriaPayload(
            historiaBase,
            facturacionValues,
            selectedService,
            patientInformation,
          )
        : historiaBase;

      await postMedicalHistory(payloadHistoria);
      setSelectedSchedule(null);
      setSelectedDoctor(null);
      setSelectedSpeciality('TODAS');
      setLoadingSaveHistory(false);

      const templateParams = {
        user_name: 'Usuario',
        reply_to: patientInformation?.correo_electronico,
        copy_to: patientInformation?.correo_electronico,
        message:
          'Estimado paciente ' +
          patientInformation?.nombres +
          ' ' +
          patientInformation?.apellido_paterno +
          ', su cita fué registrada correctamente y está pendiente de pago. La fecha de atención es: ' +
          selectedSchedule?.fecha +
          ' a las ' +
          selectedSchedule?.hora,
      };

      emailjs.send('service_zdu3k6p', 'template_3zc9p4j', templateParams, 'SuYqnE4z3_IEWAh90').then(
        (response) => {
          console.log('ÉXITO!', response.status, response.text);
        },
        (err) => {
          console.log('FALLÓ...', err);
        },
      );
    } catch (err) {
      console.error('Error guardando historia:', err);
      setLoadingSaveHistory(false);
    }
  };

  const returnMedicalHistoryForPay = (selectedDoctor, selectedSchedule, facturacionValues = null) => {
    if (!selectedService) {
      console.log('acá');
      return;
    }
    console.log('ver el paciente: ', patientInformation);

    const historiaBase = {
      codigo: selectedSchedule?.historyDetails?.codigo,
      numero_de_historia_clinica: patientInformation?.numero_de_historia_clinica,
      pacientes_codigo: patientInformation?.codigo_de_paciente,
      personal_codigo: selectedDoctor?.codigo_personal,
      tipo_de_consulta: selectedService?.codigo,
      monto_cobrado: selectedService?.costo_de_servicio,
      costo_de_consulta: selectedService?.costo_de_servicio,
      tipo_de_estado_codigo: 1,
      estado_de_horario: 'RESERVADO',
      fecha_de_atencion: selectedSchedule?.fecha,
      hora_de_atencion: selectedSchedule?.hora + ':00',
      fecha_de_registro: now.toLocaleDateString('en-CA'),
      hora_de_registro: now.toLocaleTimeString('en-GB'),
      tipo_de_reserva: 'EXTERNO',
      duracion_de_atencion: '00:30:00',
      fecha_de_reserva_atc: now.toLocaleDateString('en-CA'),
      hora_de_reserva_atc: now.toLocaleTimeString('en-GB'),
    };

    const jsonToPay = facturacionValues
      ? applyFacturacionDatosToHistoriaPayload(
          historiaBase,
          facturacionValues,
          selectedService,
          patientInformation,
        )
      : historiaBase;

    setSelectedSchedule(null);
    setSelectedDoctor(null);
    setSelectedSpeciality('TODAS');

    console.log('ver ja: ', jsonToPay);

    return jsonToPay;
  };

  return {
    isLoading,
    doctors,
    error,
    loadDoctors,
    filteredDoctorList,
    setFilteredDoctorList,
    listaEspecialidades,
    changueEspFilter,
    selectedDoctor,
    changeDoctorFilter,
    medicalHistory,
    isLoadingHistory,
    historyError,
    selectedSchedule,
    selectSchedule,
    doctorServices,
    selectedService,
    selectService,
    personalData,
    isLoadingPersonal,
    personalError,
    selectedSpeciality,
    validationSchemaFacturacion,
    saveMedicalHistory,
    patientInformation,
    setPatientInformation,
    patientsxUser,
    loadingSaveHistory,
    getPatientsForDoctor,
    isLoadingPatientsxUser,
    isLoadingSpeciality,
    isLoadingDoctor,
    setIsLoadingDoctor,
    returnMedicalHistoryForPay,
  };
};
